import { Edit, Printer } from "lucide-react";
import React, { useEffect, useState } from "react";
import useApi from "../../hooks/useApi";

const InvoiceActivityLogs = ({
  BASE_URL,
  COMPANY_CODE,
  invoiceId,
  modificationType,
  printType,
  DEPARTMENT,
}) => {
  const { request, loading } = useApi();
  const [modificationLogs, setModificationLogs] = useState([]);
  const [printLogs, setPrintLogs] = useState([]);

  const fetchLogs = async (type) => {
    const url = `${BASE_URL}/api/CRMTaskHistoryList/${COMPANY_CODE}/${type}/${DEPARTMENT}/${invoiceId}`;
    try {
      const response = await request({ url, method: "GET" });
      return response || [];
    } catch (err) {
      console.error(`Failed to fetch ${type} logs:`, err);
      return [];
    }
  };

  useEffect(() => {
    const loadLogs = async () => {
      const [mods, prints] = await Promise.all([
        fetchLogs(modificationType),
        fetchLogs(printType),
      ]);
      setModificationLogs(mods);
      setPrintLogs(prints);
    };
    if (invoiceId) {
      loadLogs();
    }
  }, [invoiceId]);

  const renderLogs = (logs, color) => {
    if (loading) {
      return <div className="text-sm text-gray-500">Loading...</div>;
    }
    if (!logs || logs.length === 0) {
      return <div className="text-sm text-gray-400">No logs available</div>;
    }
    return logs.map((log, index) => (
      <div
        key={index}
        className={`text-sm border-l-2 border-${color}-200 pl-3 py-2 mb-2`}
      >
        {log?.Comments && log?.Comments?.trim() !== "" && (
          <div className="text-gray-700 text-sm leading-relaxed mb-1 font-medium italic">
            "{log.Comments}"
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-gray-500 uppercase">{log.userid}</span>
          <div className="flex items-center gap-1.5 text-xs">
            <span className={`text-${color}-600 font-semibold`}>
              {new Date(log.create_date).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>{" "}
            <span className="text-gray-300">•</span>
            <span className="text-gray-400">
              {new Date(log.create_date).toLocaleDateString()}
            </span>
            <span className="text-gray-300">•</span>
            <span
              className={`px-1 py-0.5 text-xs font-semibold ${
                log.status?.toLowerCase() === "created"
                  ? "text-green-600"
                  : log.status?.toLowerCase() === "modified"
                  ? "text-orange-600"
                  : `text-${color}-600`
              }`}
            >
              {log.status
                ? log.status.charAt(0).toUpperCase() +
                  log.status.slice(1).toLowerCase()
                : ""}
            </span>
          </div>
        </div>
      </div>
    ));
  };

  return (
    <div className="col-span-12 lg:col-span-3 space-y-4 transition-all duration-300">
      {/* === Modification Logs === */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Edit className="w-5 h-5 text-orange-600" />
            <h3 className="font-semibold text-gray-900">Modification Logs</h3>
          </div>
        </div>
        <div className="p-4 max-h-64 overflow-y-auto scrollbar-thin">
          <div className="space-y-3">
            {renderLogs(modificationLogs, "orange")}
          </div>
        </div>
      </div>

      {/* === Print Logs === */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Printer className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900">Print Logs</h3>
          </div>
        </div>
        <div className="p-4 max-h-64 overflow-y-auto scrollbar-thin">
          <div className="space-y-3">{renderLogs(printLogs, "blue")}</div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceActivityLogs;
